/** ListSelectionBar — contextual "N selected" action bar (delete / export / set-field). */
import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Popover from '@mui/material/Popover'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import DeleteIcon from '@mui/icons-material/Delete'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import EditIcon from '@mui/icons-material/Edit'
import { FieldValueInput } from './FieldValueInput'
import type { AttributeDef } from '../designer/services/configObjectApi'

export interface ListSelectionBarProps {
  count: number; onClear: () => void; onDelete: () => void; onExport: () => void
  setFieldAttrs: AttributeDef[]; onSetField: (field: string, value: unknown) => void
  connectionId: string; busy?: boolean
}

export const ListSelectionBar: React.FC<ListSelectionBarProps> = ({ count, onClear, onDelete, onExport, setFieldAttrs, onSetField, connectionId, busy }) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const [field, setField] = useState('')
  const [value, setValue] = useState<unknown>('')
  const attr = setFieldAttrs.find((a) => a.name === field)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.selected' }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{count.toLocaleString()} selected</Typography>
      <Button size="small" disabled={busy} onClick={onClear} sx={{ textTransform: 'none' }}>Clear</Button>
      <Box sx={{ flex: 1 }} />
      {setFieldAttrs.length > 0 && (
        <Button size="small" startIcon={<EditIcon />} disabled={busy} onClick={(e) => setAnchor(e.currentTarget)} sx={{ textTransform: 'none' }}>Set field</Button>
      )}
      <Button size="small" startIcon={<FileDownloadIcon />} disabled={busy} onClick={onExport} sx={{ textTransform: 'none' }}>Export</Button>
      <Button size="small" color="error" startIcon={<DeleteIcon />} disabled={busy} onClick={onDelete} sx={{ textTransform: 'none' }}>Delete</Button>
      <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 260 }}>
          <TextField select size="small" label="Field" value={field} onChange={(e) => {
            const name = e.target.value
            setField(name)
            const a = setFieldAttrs.find((x) => x.name === name)
            const t = (a?.attr_type || 'string').toLowerCase()
            setValue(/bool/.test(t) ? false : null)
          }}>
            {setFieldAttrs.map((a) => <MenuItem key={a.name} value={a.name}>{a.synonym || a.name}</MenuItem>)}
          </TextField>
          {attr && <FieldValueInput attr={attr} value={value} onChange={setValue} connectionId={connectionId} autoFocus />}
          <Button size="small" variant="contained" disabled={!attr} onClick={() => { if (attr) { onSetField(field, value); setAnchor(null); setField(''); setValue('') } }}>
            Apply to {count.toLocaleString()}
          </Button>
        </Box>
      </Popover>
    </Box>
  )
}
